<template>
  <div class="multi-results-page">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <img src="@/assets/Icons/Loading.gif" alt="Loading" class="loading-gif" />
      <p class="loading-text">Caricamento confronto...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <h2 class="error-title">Errore</h2>
      <p class="error-text">{{ error }}</p>
      <button class="back-button" @click="goBack">
        Torna alla Dashboard
      </button>
    </div>

    <!-- Results Display -->
    <div v-else-if="analyses.length > 0" class="results-container">
      <!-- Back Button -->
      <div class="back-button-container">
        <button class="back-button" @click="goBack">
          ← Torna alla Dashboard
        </button>
      </div>

      <!-- Title Section -->
      <div class="title-section">
        <h2 class="page-title">Confronto Analisi</h2>
        <p class="subtitle">{{ analyses.length }} analisi selezionate</p>
      </div>

      <!-- Content: Radars + Stats -->
      <div class="content">
        <!-- Left Side: Single Radar Chart with All Data -->
        <div class="radars-section">
          <div class="radar-container">
            <h3 class="radar-title">Confronto Analisi</h3>
            <ComparisonRadarGraph
              :datasets="allDatasets"
              :legend="['Community', 'Storytelling', 'Experience']"
              :height="600"
              :width="600"
            />
            <!-- Legend -->
            <div class="chart-legend">
              <div
                v-for="(analysis, index) in analyses"
                :key="analysis.analysis_id"
                class="legend-item"
              >
                <div
                  class="legend-color"
                  :style="{ backgroundColor: getColor(index) }"
                ></div>
                <span class="legend-label">{{ analysis.company_name }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Side: Statistics -->
        <div class="stats-section">
          <!-- Community Stats -->
          <div class="stat-box">
            <h3 class="stat-title">Community</h3>
            <div class="stat-content">
              <div class="stat-item">
                <span class="stat-label">Media</span>
                <span class="stat-value">{{ formatScore(communityMean) }}</span>
              </div>
              <div class="stat-item highlight">
                <span class="stat-label">Top Performer</span>
                <div class="stat-value-container">
                  <span class="stat-company">{{ communityTop.company }}</span>
                  <span class="stat-value">{{ formatScore(communityTop.score) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Storytelling Stats -->
          <div class="stat-box">
            <h3 class="stat-title">Storytelling</h3>
            <div class="stat-content">
              <div class="stat-item">
                <span class="stat-label">Media</span>
                <span class="stat-value">{{ formatScore(storytellingMean) }}</span>
              </div>
              <div class="stat-item highlight">
                <span class="stat-label">Top Performer</span>
                <div class="stat-value-container">
                  <span class="stat-company">{{ storytellingTop.company }}</span>
                  <span class="stat-value">{{ formatScore(storytellingTop.score) }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Experience Stats -->
          <div class="stat-box">
            <h3 class="stat-title">Experience</h3>
            <div class="stat-content">
              <div class="stat-item">
                <span class="stat-label">Media</span>
                <span class="stat-value">{{ formatScore(experienceMean) }}</span>
              </div>
              <div class="stat-item highlight">
                <span class="stat-label">Top Performer</span>
                <div class="stat-value-container">
                  <span class="stat-company">{{ experienceTop.company }}</span>
                  <span class="stat-value">{{ formatScore(experienceTop.score) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAnalysisStore } from '@/stores/analysisStore';
import { useAuthStore } from '@/stores/authStore';
import ComparisonRadarGraph from '@/components/widgets/ComparisonRadar.vue';

export default {
  name: 'MultiAnalysisResults',
  components: {
    ComparisonRadarGraph,
  },
  setup() {
    const analysisStore = useAnalysisStore();
    return { analysisStore };
  },
  data() {
    return {
      loading: true,
      error: null,
      analyses: [],
      userId: null,
      colors: [
        '#2e4eed', // Blue
        '#10b981', // Green
        '#f59e0b', // Orange
        '#ef4444', // Red
        '#8b5cf6', // Purple
        '#ec4899', // Pink
        '#14b8a6', // Teal
        '#f97316', // Orange-red
      ],
    };
  },
  computed: {
    allDatasets() {
      return this.analyses.map((analysis, index) => ({
        data: this.getRadarData(analysis),
        color: this.getColor(index),
        label: analysis.company_name,
      }));
    },
    communityMean() {
      if (this.analyses.length === 0) return 0;
      const sum = this.analyses.reduce(
        (acc, a) => acc + (a.community_score?.community_score || 0),
        0
      );
      return sum / this.analyses.length;
    },
    storytellingMean() {
      if (this.analyses.length === 0) return 0;
      const sum = this.analyses.reduce(
        (acc, a) => acc + (a.storytelling_score?.storytelling_score || 0),
        0
      );
      return sum / this.analyses.length;
    },
    experienceMean() {
      if (this.analyses.length === 0) return 0;
      const sum = this.analyses.reduce(
        (acc, a) => acc + (a.experience_score?.experience_score || 0),
        0
      );
      return sum / this.analyses.length;
    },
    communityTop() {
      if (this.analyses.length === 0) return { company: '-', score: 0 };
      const top = this.analyses.reduce((max, a) => {
        const score = a.community_score?.community_score || 0;
        return score > (max.community_score?.community_score || 0) ? a : max;
      });
      return {
        company: top.company_name,
        score: top.community_score?.community_score || 0,
      };
    },
    storytellingTop() {
      if (this.analyses.length === 0) return { company: '-', score: 0 };
      const top = this.analyses.reduce((max, a) => {
        const score = a.storytelling_score?.storytelling_score || 0;
        return score > (max.storytelling_score?.storytelling_score || 0) ? a : max;
      });
      return {
        company: top.company_name,
        score: top.storytelling_score?.storytelling_score || 0,
      };
    },
    experienceTop() {
      if (this.analyses.length === 0) return { company: '-', score: 0 };
      const top = this.analyses.reduce((max, a) => {
        const score = a.experience_score?.experience_score || 0;
        return score > (max.experience_score?.experience_score || 0) ? a : max;
      });
      return {
        company: top.company_name,
        score: top.experience_score?.experience_score || 0,
      };
    },
  },
  async mounted() {
    // Get user ID
    const authStore = useAuthStore();
    const user = authStore.user;
    if (!user) {
      this.$router.push('/login');
      return;
    }

    this.userId = user.id;

    // Get analysis IDs from query params
    const analysisIds = this.$route.query.ids;

    if (!analysisIds) {
      this.error = 'Nessuna analisi selezionata';
      this.loading = false;
      return;
    }

    // Parse IDs (can be array or comma-separated string)
    const ids = Array.isArray(analysisIds)
      ? analysisIds
      : analysisIds.split(',');

    if (ids.length < 2) {
      this.error = 'Seleziona almeno 2 analisi per il confronto';
      this.loading = false;
      return;
    }

    // Fetch analyses
    await this.fetchAnalyses(ids);
  },
  methods: {
    async fetchAnalyses(analysisIds) {
      try {
        this.loading = true;
        this.error = null;

        // Fetch each analysis
        const promises = analysisIds.map((id) =>
          this.analysisStore.fetchAnalysisById(id, this.userId)
        );

        const results = await Promise.all(promises);

        // Filter out any failed fetches
        this.analyses = results.filter((r) => r && r.status === 'completed');

        if (this.analyses.length < 2) {
          this.error = 'Non sono state trovate abbastanza analisi completate';
        }

        this.loading = false;
      } catch (error) {
        console.error('Error fetching analyses:', error);
        this.error = error.message || 'Impossibile caricare le analisi';
        this.loading = false;
      }
    },

    getRadarData(analysis) {
      return [
        analysis.community_score?.community_score || 0,
        analysis.storytelling_score?.storytelling_score || 0,
        analysis.experience_score?.experience_score || 0,
      ];
    },

    getColor(index) {
      return this.colors[index % this.colors.length];
    },

    formatScore(score) {
      return `${Math.round(score * 10)}/100`;
    },

    goBack() {
      this.$router.push('/analisi');
    },
  },
};
</script>

<style scoped>
.multi-results-page {
  position: relative;
  min-height: 100vh;
  padding: 2rem;
  padding-top: 120px;
  background: linear-gradient(135deg, #ffffff, #56bcee);
}

.loading-container,
.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  gap: 1.5rem;
  text-align: center;
  max-width: 600px;
  margin: 0 auto;
}

.loading-gif {
  width: 60px;
  height: 60px;
}

.loading-text {
  font-size: 1.125rem;
  color: #1a1a1a;
}

.error-title {
  font-size: 2rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0;
}

.error-text {
  font-size: 1.125rem;
  color: #ef4444;
  margin: 0;
}

.back-button {
  background: #2e4eed;
  border: 2px solid #2e4eed;
  color: white;
  padding: 0.75rem 1.5rem;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.back-button:hover {
  background: #1e3ed8;
  border-color: #1e3ed8;
}

.results-container {
  max-width: 1800px;
  margin: 0 auto;
}

.back-button-container {
  margin-bottom: 2rem;
}

.title-section {
  width: 100%;
  margin-bottom: 3rem;
  text-align: left;
}

.page-title {
  font-size: 3rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.subtitle {
  font-size: 1.25rem;
  color: #6b7280;
  margin: 0;
}

.content {
  display: flex;
  gap: 4rem;
  width: 100%;
}

.radars-section {
  flex: 1;
  display: flex;
  justify-content: center;
}

.radar-container {
  background: rgba(255, 255, 255, 0.95);
  border: 2px solid #2e4eed;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 800px;
}

.radar-title {
  font-size: 1.75rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 2rem 0;
  text-align: center;
}

.chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  justify-content: center;
  margin-top: 2rem;
  padding-top: 2rem;
  border-top: 2px solid #e5e7eb;
  width: 100%;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.legend-color {
  width: 24px;
  height: 24px;
  border: 2px solid #1a1a1a;
  flex-shrink: 0;
}

.legend-label {
  font-size: 1rem;
  font-weight: 500;
  color: #1a1a1a;
}

.stats-section {
  flex: 0 0 400px;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  position: sticky;
  top: 140px;
  align-self: flex-start;
}

.stat-box {
  background: rgba(255, 255, 255, 0.95);
  border: 2px solid #2e4eed;
  padding: 1.5rem;
}

.stat-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1.5rem 0;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e5e7eb;
}

.stat-content {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.stat-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: rgba(46, 78, 237, 0.05);
  border: 1px solid #e5e7eb;
}

.stat-item.highlight {
  background: rgba(46, 78, 237, 0.1);
  border: 2px solid #2e4eed;
}

.stat-label {
  font-size: 1rem;
  font-weight: 500;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.stat-value {
  font-size: 1.5rem;
  font-weight: 600;
  color: #2e4eed;
}

.stat-value-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.25rem;
}

.stat-company {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
}

@media (max-width: 1200px) {
  .content {
    flex-direction: column;
    gap: 3rem;
  }

  .stats-section {
    position: static;
    flex: 1;
    width: 100%;
    max-width: 600px;
    margin: 0 auto;
  }
}

@media (max-width: 768px) {
  .multi-results-page {
    padding: 1rem;
    padding-top: 100px;
  }

  .page-title {
    font-size: 2rem;
  }

  .subtitle {
    font-size: 1rem;
  }

  .radar-container {
    padding: 1rem;
  }

  .radar-title {
    font-size: 1.25rem;
  }

  .chart-legend {
    gap: 1rem;
    margin-top: 1.5rem;
    padding-top: 1.5rem;
  }

  .legend-color {
    width: 20px;
    height: 20px;
  }

  .legend-label {
    font-size: 0.875rem;
  }

  .stat-box {
    padding: 1rem;
  }

  .stat-title {
    font-size: 1.25rem;
  }

  .stat-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .stat-value-container {
    align-items: flex-start;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 1.75rem;
  }

  .stat-value {
    font-size: 1.25rem;
  }
}
</style>
